import { createFileRoute } from "@tanstack/react-router";
import { ResearchBlock } from "@/components/site/ResearchBlock";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad är ett filmbolag?",
    a: "Ett filmbolag är ett företag som producerar film — antingen för bio och tv (spelfilm, drama, dokumentär) eller för uppdragsgivare (reklamfilm, företagsfilm, kampanjfilm). I dagligt tal i Sverige används filmbolag, produktionsbolag och filmbyrå ofta synonymt, men de beskriver olika roller i kedjan.",
  },
  {
    q: "Vad är skillnaden mellan filmbolag, produktionsbolag och filmbyrå?",
    a: "Filmbolag är den bredaste termen och används både om långfilmsbolag och om uppdragsproduktion. Produktionsbolag utgår oftast från ett färdigt manus eller en given idé och ansvarar för genomförandet. En filmbyrå tar dessutom ansvar för strategi, koncept och distribution. Fenny arbetar som filmbyrå men driver hela produktionen inhouse.",
  },
  {
    q: "Vad kostar det att anlita ett filmbolag?",
    a: "Kortare företagsfilm och social-content ligger typiskt på 40 000–120 000 kr. Reklamfilm 15–30 sekunder landar oftast på 150 000–450 000 kr. Brand-film och dokumentär 250 000–700 000 kr. Löpande leverans via Pulse-abonnemang startar på 14 995 kr/mån.",
  },
  {
    q: "Hur väljer man rätt filmbolag?",
    a: "Titta på tre saker: relevanta case i er kategori (inte bara vacker reel), vem som faktiskt gör jobbet (inhouse-team eller inhyrda frilansare per projekt) och hur de resonerar om mål och distribution innan de pratar om kamera. Be alltid om fast pris och tydlig leveransspecifikation.",
  },
  {
    q: "Producerar Fenny spelfilm och tv-drama?",
    a: "Nej. Fenny är ett filmbolag för uppdragsfilm: reklamfilm, företagsfilm, employer branding, kundcase, dokumentär för varumärken och löpande social-content. Vi gör inte biofilm eller tv-drama.",
  },
  {
    q: "Hur snabbt kan ni leverera?",
    a: "Social-content 2–3 veckor från brief. Reklamfilm 4–6 veckor. Brand-film och dokumentär 5–8 veckor. Med Pulse-abonnemang levereras första klippet inom 10–14 dagar från strategimötet.",
  },
];

export const Route = createFileRoute("/filmbolag")({
  head: () => ({
    meta: [
      { title: "Filmbolag — priser 2026 och 5 frågor som avslöjar kvaliteten" },
      {
        name: "description",
        content:
          "Vad kostar ett filmbolag och hur väljer ni rätt? Prisspann för 2026, fem frågor som avslöjar kvalitet och vad som ska ingå innan ni skriver på.",
      },

      { property: "og:title", content: "Filmbolag — priser 2026 och 5 frågor som avslöjar kvaliteten" },
      {
        property: "og:description",
        content:
          "Skillnaden mellan filmbolag, produktionsbolag och filmbyrå — plus prisspann och hur ni väljer rätt.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/filmbolag" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/filmbolag" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/filmbolag", name: "Filmbolag i Sverige — vad de gör och vad det kostar | Fenny", description: "Filmbolag, produktionsbolag eller filmbyrå? Vi reder ut skillnaden, vad uppdragsfilm kostar och vilka fem frågor ni bör ställa innan ni skriver på."})) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Filmbolag för uppdragsfilm",
            path: "/filmbolag",
            serviceType: "Filmproduktion",
            description:
              "Svenskt filmbolag för reklamfilm, företagsfilm, employer branding och dokumentär — hela produktionskedjan inhouse.",
            priceRange: "40 000–700 000 kr",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Filmbolag", path: "/filmbolag" },
          ]),
        ),
      },
    ],
  }),
  component: FilmbolagPage,
});

function FilmbolagPage() {
  return (
    <LandingPage
      eyebrow="Filmbolag"
      title={<>Filmbolag för uppdragsfilm — hela kedjan under ett tak.</>}
      lede="Fenny är ett svenskt filmbolag som producerar film på uppdrag: reklamfilm, företagsfilm, employer branding, kundcase och dokumentär. Regi, foto, ljud, klipp och grade ligger inhouse — därför blir kvaliteten densamma oavsett vem som råkar vara ledig den veckan."
      tldr={
        <p>
          Ett filmbolag producerar film — för bio och tv, eller på uppdrag av
          varumärken. Fenny är ett filmbolag för uppdragsfilm med eget team i
          Stockholm, Göteborg och Malmö. Pris: <strong>40 000–700 000 kr</strong>{" "}
          per produktion beroende på format, eller{" "}
          <strong>Pulse-abonnemang från 14 995 kr/mån</strong> för löpande flöde.
        </p>
      }
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Se case", to: "/case" }}
      trackingId="filmbolag"
      bullets={[
        {
          title: "Eget produktionsteam",
          body: "Regi, foto, ljud, klipp, grade. Ingen sammansatt frilanskedja per projekt.",
        },
        {
          title: "Fast pris",
          body: "Offert med fast pris och tidsplan inom 48 timmar efter första samtalet.",
        },
        {
          title: "Hela Sverige",
          body: "Team i Stockholm, Göteborg och Malmö. Resa inom Sverige ingår i abonnemang.",
        },
      ]}
      pricing={{
        heading: "Vad kostar ett filmbolag 2026?",
        intro:
          "Riktpriser exklusive moms för uppdragsfilm i Sverige. Exakt pris sätts efter brief och produktionsplan.",
        rows: [
          {
            format: "Social-content / kortformat",
            scope: "1 inspelningsdag, flera klipp, versionering",
            price: "40 000–120 000 kr",
            lead: "2–3 veckor",
          },
          {
            format: "Företagsfilm",
            scope: "Manus, 1–2 dagar inspelning, klipp och grade",
            price: "80 000–250 000 kr",
            lead: "3–5 veckor",
          },
          {
            format: "Reklamfilm 15–30 sek",
            scope: "Koncept, casting, regi, post, versionering",
            price: "150 000–450 000 kr",
            lead: "4–6 veckor",
          },
          {
            format: "Brand-film / dokumentär",
            scope: "Research, flera inspelningsdagar, musik och ljudmix",
            price: "250 000–700 000 kr",
            lead: "5–8 veckor",
          },
          {
            format: "Pulse-abonnemang",
            scope: "Löpande produktion, 2–8 klipp per månad",
            price: "från 14 995 kr/mån",
            lead: "första klipp inom 10–14 dagar",
          },
        ],
        footnote:
          "Alla priser exklusive moms. Löpande behov blir nästan alltid billigare per film via abonnemang än som separata projekt.",
      }}
      process={{
        heading: "Så jobbar ett filmbolag — steg för steg",
        steps: [
          { label: "Brief och mål", body: "Vad filmen ska åstadkomma, för vem och var den ska publiceras.", duration: "vecka 1" },
          { label: "Koncept och manus", body: "Idé, manus, storyboard och skriftligt godkännande innan kamera bokas.", duration: "vecka 1–2" },
          { label: "Pre-production", body: "Casting, location, tillstånd, schema och teknikplan.", duration: "vecka 2–3" },
          { label: "Inspelning", body: "Regi, foto, ljus och ljud med eget team. Typiskt 1–2 dagar.", duration: "vecka 3–4" },
          { label: "Post och leverans", body: "Klipp, grade, ljudmix, undertexter och versionering för alla format.", duration: "vecka 4–6" },
        ],
      }}
      sections={[
        {
          id: "jamforelse",
          heading: "Innan ni väljer",
          body: (
            <ResearchBlock
              intro={"De flesta som läser den här sidan jämför tre till fem bolag under några veckor. Här är det ni behöver för att kunna göra jämförelsen utan att boka ett enda möte."}
              steps={[
                { q: "Sätt formatet först", a: "Reklamfilm, företagsfilm och löpande social-content har helt olika kostnadsbild. Bestäm formatet innan ni jämför offerter — annars jämför ni äpplen och päron." },
                { q: "Kolla vem som gör jobbet", a: "Fråga rakt ut hur stor del av teamet som är inhouse. Frilanskedjor per projekt ger ojämn kvalitet mellan filmer." },
                { q: "Be om leveransspecifikation", a: "Antal versioner, format, revisionsrundor och rättigheter ska stå svart på vitt innan ni väljer." },
              ]}
              links={[
                { label: "Se prisspann och exempel", href: "/priser" },
                { label: "Filmbyrå vs frilansare", href: "/jamfor/filmbyra-vs-frilansare" },
                { label: "Vanliga frågor", href: "/faq" },
              ]}
            />
          ),
        },
        {
          id: "vad-ar-ett-filmbolag",
          heading: "Vad är ett filmbolag?",
          body: (
            <>
              <p>
                Ett filmbolag är ett företag som producerar film. Begreppet
                används både om bolag som gör spelfilm och tv-drama och om bolag
                som producerar film på uppdrag av företag och organisationer.
                Fenny hör till den andra kategorin: vi gör film som ska sälja,
                rekrytera, förklara eller bygga varumärke.
              </p>
              <p>
                Ett filmbolag tar ansvar för hela kedjan — pre-production
                (manus, casting, planering), production (regi, foto, ljus, ljud)
                och post-production (klipp, grade, ljudmix, versionering) — och
                levererar färdigt material redo att publiceras.
              </p>
            </>
          ),
        },
        {
          id: "skillnad",
          heading: "Filmbolag, produktionsbolag eller filmbyrå?",
          body: (
            <>
              <p>
                Orden används om vartannat, men skillnaden spelar roll när ni
                handlar upp. Ett{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/produktionsbolag-film">
                  produktionsbolag
                </a>{" "}
                utgår från en given idé och ansvarar för att den blir film. En{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmbyra">
                  filmbyrå
                </a>{" "}
                tar även fram strategi, koncept och distributionsplan. Filmbolag
                är paraplybegreppet.
              </p>
              <p>
                Har ni ett färdigt manus räcker ett produktionsbolag. Har ni ett
                affärsmål men ingen idé ännu vill ni ha byrådelen med — annars
                betalar ni för en vacker film som inte gör något.
              </p>
            </>
          ),
        },
        {
          id: "valja",
          heading: "Så väljer ni rätt filmbolag",
          body: (
            <>
              <p>
                Be om case i er kategori, inte bara en reel. Fråga vilka som
                faktiskt utför jobbet — fast team eller frilansare som bokas per
                projekt. Kontrollera att offerten är fast pris med tydlig
                leveransspecifikation: antal versioner, antal revisionsrundor,
                rättigheter och format.
              </p>
              <p>
                Jämför också modell:{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/jamfor/filmbyra-vs-frilansare">
                  filmbyrå vs frilansare
                </a>{" "}
                och{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/jamfor/filmabonnemang-vs-projektfilm">
                  abonnemang vs projektfilm
                </a>
                .
              </p>
            </>
          ),
        },
        {
          id: "var",
          heading: "Var vi producerar",
          body: (
            <p>
              Vi producerar i hela Sverige med bas i{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/stockholm">Stockholm</a>,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/goteborg">Göteborg</a> och{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/malmo">Malmö</a>.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={6}
      riskReducers
      closing={{
        title: "Letar ni efter ett filmbolag?",
        body: "Boka 30 minuter så säger vi rakt ut om vi är rätt för uppdraget — och vad det skulle kosta.",
        primary: { label: "Boka strategimöte", to: "/kontakt" },
        secondary: { label: "Se priser", to: "/priser" },
      }}
    />
  );
}
